(() => {
  'use strict';

  const MOBILE_QUERY = '(max-width: 767px)';
  const FIT_MESSAGES = {
    exact: 'Elige tu talla habitual para respetar el corte diseñado de esta prenda.',
    oversized: 'Sube una talla para conseguir una silueta más amplia y una caída extra.',
  };

  document.addEventListener('click', (event) => {
    const option = event.target.closest('[data-fit-mode]');
    if (!option) return;

    const selector = option.closest('[data-zionic-fit-selector]');
    if (!selector) return;

    selector.querySelectorAll('[data-fit-mode]').forEach((button) => {
      const isActive = button === option;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    const hint = selector.querySelector('[data-fit-hint]');
    const mode = option.dataset.fitMode;
    if (hint && FIT_MESSAGES[mode]) hint.textContent = FIT_MESSAGES[mode];
  });

  if (customElements.get('zionic-sticky-atc')) return;

  class ZionicStickyAtc extends HTMLElement {
    constructor() {
      super();
      this.visibilityFrame = null;
      this.sourceButton = null;
      this.sourceButtonObserver = null;
      this.visibilityObserver = null;
      this.unsubscribeVariantChange = null;
      this.handleViewportChange = this.queueVisibilityUpdate.bind(this);
      this.handleProxyClick = this.handleProxyClick.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
    }

    connectedCallback() {
      if (this.closest('quick-add-modal')) return;

      this.sectionId = this.dataset.sectionId;
      this.productInfo = this.closest('product-info');
      this.proxyButton = this.querySelector('[data-zionic-sticky-submit]');
      this.proxyLabel = this.querySelector('[data-zionic-sticky-label]');
      this.variantLabel = this.querySelector('[data-zionic-sticky-variant]');
      this.priceLabel = this.querySelector('[data-zionic-sticky-price]');

      if (!this.sectionId || !this.productInfo || !this.proxyButton) return;

      this.proxyButton.addEventListener('click', this.handleProxyClick);
      window.addEventListener('scroll', this.handleViewportChange, { passive: true });
      window.addEventListener('resize', this.handleViewportChange, { passive: true });

      this.connectSourceButton();
      this.subscribeToVariantChanges();
      this.queueVisibilityUpdate();
    }

    disconnectedCallback() {
      this.proxyButton?.removeEventListener('click', this.handleProxyClick);
      window.removeEventListener('scroll', this.handleViewportChange);
      window.removeEventListener('resize', this.handleViewportChange);
      this.sourceButtonObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.unsubscribeVariantChange?.();
      if (this.visibilityFrame) cancelAnimationFrame(this.visibilityFrame);
    }

    connectSourceButton() {
      const nextSourceButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (!nextSourceButton || nextSourceButton === this.sourceButton) {
        this.syncFromSourceButton();
        return;
      }

      this.sourceButtonObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.sourceButton = nextSourceButton;

      this.sourceButtonObserver = new MutationObserver(() => {
        this.syncFromSourceButton();
        this.queueVisibilityUpdate();
      });
      this.sourceButtonObserver.observe(this.sourceButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
        childList: true,
        subtree: true,
      });

      this.visibilityObserver = new IntersectionObserver(
        () => this.queueVisibilityUpdate(),
        { threshold: [0, 1] }
      );
      this.visibilityObserver.observe(this.sourceButton);
      this.syncFromSourceButton();
    }

    subscribeToVariantChanges() {
      if (
        typeof window.subscribe !== 'function' &&
        typeof subscribe !== 'function'
      ) {
        return;
      }

      if (typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.variantChange) return;

      const subscribeToEvent = typeof window.subscribe === 'function' ? window.subscribe : subscribe;
      this.unsubscribeVariantChange = subscribeToEvent(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
    }

    handleVariantChange(event) {
      const data = event?.data;
      if (!data || data.sectionId !== this.sectionId) return;

      const variant = data.variant;
      if (this.variantLabel) {
        this.variantLabel.textContent =
          variant?.title && variant.title !== 'Default Title' ? `${variant.title} · ` : '';
      }

      if (variant && this.priceLabel) {
        this.priceLabel.textContent = this.formatMoney(variant.price);
      }

      if (this.proxyButton) {
        const unavailable = !variant || !variant.available;
        this.proxyButton.disabled = unavailable;
        if (this.proxyLabel) {
          this.proxyLabel.textContent = unavailable
            ? window.variantStrings?.soldOut || 'Agotado'
            : window.variantStrings?.addToCart || 'Añadir al carrito';
        }
      }

      requestAnimationFrame(() => {
        this.connectSourceButton();
        this.syncFromSourceButton();
      });
    }

    formatMoney(cents) {
      const numericCents = Number(cents);
      const currency = window.Shopify?.currency?.active;
      if (!Number.isFinite(numericCents) || !currency) return this.priceLabel?.textContent || '';

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'es', {
          style: 'currency',
          currency,
        }).format(numericCents / 100);
      } catch (_error) {
        return this.priceLabel?.textContent || '';
      }
    }

    syncFromSourceButton() {
      if (!this.sourceButton || !this.proxyButton) return;

      const isDisabled =
        this.sourceButton.disabled || this.sourceButton.getAttribute('aria-disabled') === 'true';
      this.proxyButton.disabled = isDisabled;
      this.proxyButton.classList.toggle('loading', this.sourceButton.classList.contains('loading'));

      const sourceLabel = Array.from(this.sourceButton.children).find(
        (child) => child.tagName === 'SPAN' && !child.classList.contains('loading__spinner')
      );
      if (sourceLabel && this.proxyLabel) {
        const label = sourceLabel.textContent.trim();
        if (label) this.proxyLabel.textContent = label;
      }
    }

    handleProxyClick() {
      this.connectSourceButton();
      if (!this.sourceButton || this.proxyButton.disabled) return;
      this.sourceButton.click();
    }

    queueVisibilityUpdate() {
      if (this.visibilityFrame) return;
      this.visibilityFrame = requestAnimationFrame(() => {
        this.visibilityFrame = null;
        this.updateVisibility();
      });
    }

    updateVisibility() {
      const isMobile = window.matchMedia(MOBILE_QUERY).matches;
      const sourceRect = this.sourceButton?.getBoundingClientRect();
      const productRect = this.productInfo?.getBoundingClientRect();
      const shouldShow = Boolean(
        isMobile &&
          sourceRect &&
          productRect &&
          sourceRect.bottom < 0 &&
          productRect.bottom > 80
      );

      this.classList.toggle('is-visible', shouldShow);
      this.setAttribute('aria-hidden', String(!shouldShow));
    }
  }

  customElements.define('zionic-sticky-atc', ZionicStickyAtc);
})();
